// AppContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { FileIcon, defaultStyles } from 'react-file-icon';

const AppContext = createContext();

export const useAppContext = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const initialCollapsed = window.innerWidth < 1281;
    return JSON.parse(localStorage.getItem("isCollapsed")) ?? initialCollapsed;
  });

  const [data, setData] = useState([]);
  const [dataIsLoading, setDataIsLoading] = useState(true);

  const [onboardingData, setOnboardingData] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [hiddenTasks, setHiddenTasks] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  const [tableSize, setTableSize] = useState("sm");
  const toggleTableSize = () => {
    setTableSize((prevSize) => (prevSize === "sm" ? "md" : "sm"));
  };

  // Load data from JSON files on component mount
  useEffect(() => {
    fetch("/dummyData/funds.json")
      .then((response) => response.json())
      .then((data) => setData(data));

    fetch("/dummyData/onboardingFunds.json")
      .then((response) => response.json())
      .then((onboardingData) => setOnboardingData(onboardingData));

    fetch("/dummyData/tasks.json")
      .then((response) => response.json())
      .then((data) => {
        setTasks(data);
        setHiddenTasks(data.filter((task) => !task.completed).slice(8));
      });

      fetch("/dummyData/currencies.json")
      .then((response) => response.json())
      .then((currencyData) => setCurrencies(currencyData));
  }, []);


  const formatMillions = (number) => {
    if (number >= 100_000) {
      const formattedMillions = (number / 1_000_000).toFixed(1); // Convert to millions with one decimal place
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(formattedMillions) + 'M'; // Format with US-style separators
    }
    return new Intl.NumberFormat("en-US").format(number); // Format numbers below 100,000 normally
  };
  
  

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase();

    return (
      <FileIcon
        extension={extension}
        {...defaultStyles[extension]}
      />
    );
  };

  const remainingTasks = tasks.filter((task) => !task.completed).length;


  const [riskScores, setRiskScores] = useState({
    businessRisk: { businessModel: 0, managementExperience: 0, ownershipStructure: 0 },
    marketRisk: { strategyComplexity: 0, marketVolatility: 0, liquidityProfile: 0 },
    operationalRisk: { processRobustness: 0, serviceProviderQuality: 0, systemsAndControls: 0 },
    complianceRisk: { regulatoryFramework: 0, investmentRestrictions: 0, investorBase: 0 },
  });



  const value = {
    isCollapsed,
    setIsCollapsed,
    data,
    dataIsLoading,
    setDataIsLoading,
    onboardingData,
    formatMillions,
    tasks, setTasks,
    hiddenTasks,
    setHiddenTasks,
    remainingTasks,
    formatDate,
    tableSize,
    setTableSize,
    toggleTableSize,
    currencies,
    getFileIcon,
    riskScores,
    setRiskScores
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
